import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { DraftManual, ManualPermissions } from 'views/App/Portal/Policy/_types/manuals/draft';
import useStickyState from 'hooks/useStickyState';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';

type DraftManualGIQProviderProps = {
  children: React.ReactNode;
};

export type DraftManualGIQProviderResponse = {
  manualPermissions: ManualPermissions | null;
  setManualPermissions: (manualPermissions: ManualPermissions | null) => void;
  manualHasPermissions: (manualId: string, keys: string[]) => boolean;
  manualId: string;
  manual: DraftManual;
  manualQuery: any;
};

export const DraftManualGIQContext = React.createContext<DraftManualGIQProviderResponse>(
  {} as DraftManualGIQProviderResponse
);

const DraftManualGIQProvider: ({ children }: DraftManualGIQProviderProps) => any = ({ children }) => {
  const { id } = useParams();
  const manualQuery = useDraftManual(id, {
    onSuccess: (data: DraftManual) => {
      setManualPermissions((prev) => {
        prev = prev ?? {};
        prev[data.id] = (data.authorizations ?? []).map((x) => x.action).sort();
        return prev;
      });
    },
  });
  const [manualPermissions, setManualPermissions] = useStickyState<ManualPermissions | null>(
    'phx-manualPermissions',
    null
  );

  const manualHasPermissions = useCallback(
    (manualId: string, keys: string[]) => {
      let permissionKeys: string[] = [];
      if (manualPermissions && manualPermissions[manualId]) {
        permissionKeys = manualPermissions[manualId];
      }
      return keys.every((x) => permissionKeys.map((x) => x.toLowerCase()).includes(x.toLowerCase()));
    },
    [manualPermissions]
  );

  const manual = useMemo(() => {
    return manualQuery.data ?? ({} as DraftManual);
  }, [manualQuery.data]);

  const contextValue: DraftManualGIQProviderResponse = useMemo(
    () => ({
      manualPermissions,
      manualHasPermissions,
      setManualPermissions,
      manualId: id ?? '',
      manual,
      manualQuery,
    }),
    [manualPermissions, manualHasPermissions, setManualPermissions, id, manual, manualQuery]
  );

  return <DraftManualGIQContext.Provider value={contextValue}>{children}</DraftManualGIQContext.Provider>;
};

export default DraftManualGIQProvider;
